import { ZodIssue, ZodSchema } from 'zod';
import {
  APIClientConfig,
  APIRequestOptions,
  DSLConfig,
  DSLConfigListSchema,
  DSLConfigSchema,
  Flow,
  FlowSchema,
  ListDSLConfigsOptions,
  Question,
  QuestionSchema,
} from './dynamicFlowsSchema';

export class DynamicFlowsApiClient {
  /**
   * The cache for the API client.
   */
  public cache: Map<string, Response> = new Map();
  public readonly config: APIClientConfig;

  constructor(config: APIClientConfig) {
    const defaultConfig: APIClientConfig = {
      baseUrl: '',
      defaultHeaders: {},
      cache: new Map(),
      newRelic: {
        noticeError: (error: Error, context: Record<string, any>) => {
          console.error(
            'Error in DynamicFlowsApiClient: noticeError',
            error,
            context,
          );
        },
      },
      strictValidation: false,
    };
    this.config = { ...defaultConfig, ...config };
  }

  public getApiName(): string {
    return 'dynamic-flows';
  }

  private async get(
    path: string,
    options: APIRequestOptions = { cacheEnabled: false },
  ): Promise<Response> {
    const cacheKey = `${this.config.baseUrl}${path}`;

    if (options.bustCache) {
      this.bustCache(cacheKey);
    }

    if (this.shouldUseCache(cacheKey, options)) {
      const cached = this.cache.get(cacheKey);
      if (cached) {
        return cached.clone();
      }
    }

    const response = await fetch(`${this.config.baseUrl}${path}`, {
      method: 'GET',
      ...options,
    });

    if (options.cacheEnabled && response.ok) {
      this.cache.set(cacheKey, response.clone());
    }
    return response;
  }

  private bustCache(cacheKey: string): void {
    this.cache.delete(cacheKey);
  }

  private shouldUseCache(
    cacheKey: string,
    options: APIRequestOptions,
  ): boolean {
    if (!options.cacheEnabled) {
      return false;
    }
    return this.cache.has(cacheKey);
  }

  private hasError(response: Response): boolean {
    return response.status >= 400;
  }

  /**
   * Converts the response to a JSON object that is defined by the passed in schema.
   */
  public async toJson<T>(response: Response, schema: ZodSchema<T>): Promise<T> {
    const data = await response.json();
    // Validate the full response as-is
    const validated = schema.safeParse(data);

    if (!validated.success) {
      const error = new Error(`Invalid response for ${this.getApiName()}`);
      console.error(error);
      console.log(this.buildErrorMessage(validated.error.errors));

      if (this.config.strictValidation) {
        throw error;
      }
      return data as T;
    }
    return validated.data;
  }

  private buildErrorMessage(errors: ZodIssue[]): string[] {
    return errors.map(
      (error) => `Path: ${error.path.join('.')}, Message: ${error.message}`,
    );
  }

  /**
   * Get a flow configuration.
   * @param tenantId - The tenant ID.
   * @param flowId - The flow ID.
   * @param requestHeaders - Optional request headers.
   * @param options - Optional request options.
   * @returns The flow configuration.
   */
  async getFlow(
    tenantId: string,
    flowId: string,
    requestHeaders: Record<string, string> = {},
    options: APIRequestOptions = { cacheEnabled: false },
  ): Promise<Flow> {
    const path = `/v1/tenants/${encodeURIComponent(tenantId)}/flows/${encodeURIComponent(flowId)}`;

    const response = await this.get(path, {
      ...options,
      headers: {
        ...this.config.defaultHeaders,
        ...requestHeaders,
      },
    });

    if (this.hasError(response)) {
      console.error(
        `Failed to get flow for tenantId: ${tenantId}, flowId: ${flowId}`,
      );
      throw new Error(
        `Failed to get flow: ${response.status} ${response.statusText}`,
      );
    }

    const data = await this.toJson(response, FlowSchema);
    return data;
  }

  /**
   * List DSL configs for a tenant using the existing read-only API.
   * @param tenantId - The tenant ID.
   * @param filters - Optional type/status filters.
   * @param requestHeaders - Optional request headers.
   * @param options - Optional request options.
   * @returns The matching DSL configs.
   */
  async listDslConfigs(
    tenantId: string,
    filters: ListDSLConfigsOptions = {},
    requestHeaders: Record<string, string> = {},
    options: APIRequestOptions = { cacheEnabled: false },
  ): Promise<DSLConfig[]> {
    const params = new URLSearchParams();
    if (filters.type) params.set('type', filters.type);
    if (filters.status) params.set('status', filters.status);

    const query = params.toString();
    const path = `/v1/tenants/${encodeURIComponent(tenantId)}/dsl-configs${
      query ? `?${query}` : ''
    }`;

    const response = await this.get(path, {
      ...options,
      headers: {
        ...this.config.defaultHeaders,
        ...requestHeaders,
      },
    });

    if (this.hasError(response)) {
      console.error(`Failed to list DSL configs for tenantId: ${tenantId}`);
      throw new Error(
        `Failed to list DSL configs: ${response.status} ${response.statusText}`,
      );
    }

    return this.toJson(response, DSLConfigListSchema);
  }

  /**
   * Get a DSL config by ID using the existing read-only API.
   * @param tenantId - The tenant ID.
   * @param configId - The DSL config ID.
   * @param requestHeaders - Optional request headers.
   * @param options - Optional request options.
   * @returns The DSL config payload.
   */
  async getDslConfig(
    tenantId: string,
    configId: string,
    requestHeaders: Record<string, string> = {},
    options: APIRequestOptions = { cacheEnabled: false },
  ): Promise<DSLConfig> {
    const path = `/v1/tenants/${encodeURIComponent(tenantId)}/dsl-configs/${encodeURIComponent(configId)}`;

    const response = await this.get(path, {
      ...options,
      headers: {
        ...this.config.defaultHeaders,
        ...requestHeaders,
      },
    });

    if (this.hasError(response)) {
      console.error(
        `Failed to get DSL config for tenantId: ${tenantId}, configId: ${configId}`,
      );
      throw new Error(
        `Failed to get DSL config: ${response.status} ${response.statusText}`,
      );
    }

    return this.toJson(response, DSLConfigSchema);
  }

  /**
   * Get a question by ID.
   * @param tenantId - The tenant ID.
   * @param questionId - The question ID.
   * @param requestHeaders - Optional request headers.
   * @param options - Optional request options.
   * @returns The question, or null if not found or an error occurred.
   */
  async getQuestion(
    tenantId: string,
    questionId: string,
    requestHeaders: Record<string, string> = {},
    options: APIRequestOptions = { cacheEnabled: false },
  ): Promise<Question | null> {
    const path = `/v1/tenants/${encodeURIComponent(tenantId)}/questions/${encodeURIComponent(questionId)}`;

    const response = await this.get(path, {
      ...options,
      headers: {
        ...this.config.defaultHeaders,
        ...requestHeaders,
      },
    });

    if (this.hasError(response)) {
      console.error(
        `Error fetching question ${questionId}:`,
        response.statusText,
      );
      return null;
    }

    const data = await this.toJson(response, QuestionSchema);
    return {
      id: data.id,
      type: data.type,
      title: data.content,
      subtitle: data.subtitle,
      options: data.options?.map((option) => option.text),
      placeholders: data.placeholders,
      questionType: data.questionType,
    };
  }
}
